import { Component } from 'react';
import { CalcResultBar } from '../calc-result-bar.component';
import sharedStyles from '../single-page.module.scss';
import styles from './aspect-ratio-calculator.component.module.scss';

interface AspectState {
    width: number;
    height: number;
    locked: boolean;
    lockedRatio: number; // width/height when locked
}

const PRESETS = [
    { label: '16:9', w: 16, h: 9 },
    { label: '4:3', w: 4, h: 3 },
    { label: '1:1', w: 1, h: 1 },
    { label: '21:9', w: 21, h: 9 },
    { label: '9:16', w: 9, h: 16 },
];

function gcd(a: number, b: number): number {
    a = Math.abs(Math.round(a));
    b = Math.abs(Math.round(b));
    while (b) { [a, b] = [b, a % b]; }
    return a;
}

function formatRatio(w: number, h: number): string {
    if (!w || !h) return '—';
    const d = gcd(w, h);
    return `${w / d}:${h / d}`;
}

export default class AspectRatioCalculatorComponent extends Component<{ feature?: string }, AspectState> {
    state: AspectState = { width: 1920, height: 1080, locked: false, lockedRatio: 16 / 9 };

    setWidth = (e: React.ChangeEvent<HTMLInputElement>) => {
        const width = Math.max(0, +e.target.value || 0);
        if (this.state.locked) {
            this.setState({ width, height: Math.round(width / this.state.lockedRatio) });
        } else {
            this.setState({ width });
        }
    };

    setHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
        const height = Math.max(0, +e.target.value || 0);
        if (this.state.locked) {
            this.setState({ height, width: Math.round(height * this.state.lockedRatio) });
        } else {
            this.setState({ height });
        }
    };

    toggleLock = () => {
        const { width, height, locked } = this.state;
        this.setState({ locked: !locked, lockedRatio: width / (height || 1) });
    };

    applyPreset = (w: number, h: number) => {
        this.setState({ width: w, height: h, locked: true, lockedRatio: w / h });
    };

    render() {
        const { width, height, locked } = this.state;
        const ratio = formatRatio(width, height);
        const activePreset = PRESETS.find(p => formatRatio(width, height) === `${p.w}:${p.h}`);

        return (
            <>
                <CalcResultBar />
                <div className="row">
                <div className="col-12">
                    <label className={sharedStyles.label}>Presets</label>
                    <div className={styles.presets}>
                        {PRESETS.map(p => (
                            <button key={p.label}
                                className={`${styles.preset} ${activePreset?.label === p.label ? styles.presetActive : ''}`}
                                onClick={() => this.applyPreset(p.w, p.h)}>{p.label}</button>
                        ))}
                    </div>

                    <div className={styles.inputRow}>
                        <input type="number" inputMode="decimal" className={styles.dimInput} value={width} onChange={this.setWidth}
                            min={0} aria-label="Width" placeholder="Width" />
                        <span className={styles.separator}>×</span>
                        <input type="number" inputMode="decimal" className={styles.dimInput} value={height} onChange={this.setHeight}
                            min={0} aria-label="Height" placeholder="Height" />
                        <button className={`${styles.lockBtn} ${locked ? styles.lockActive : ''}`}
                            onClick={this.toggleLock}>{locked ? '🔒 Locked' : '🔓 Unlocked'}</button>
                    </div>

                    <div className={styles.ratio}>Ratio: {ratio}</div>
                    <button className={sharedStyles.copyButton} style={{ marginTop: '0.75rem' }}
                        onClick={() => navigator.clipboard?.writeText(`${width}×${height} (${ratio})`)}>
                        📋 Copy result
                    </button>
                </div>
            </div>
            </>
        );
    }
}
